import {
  AthleteAndRolesRes, ATHLETES, ATHLETES_AND_ROLES, AthleteTypeRes, AthleteTypeVars,
} from 'query/athlete';
import React, { useEffect, useMemo, useRef } from 'react';
import { OptionType } from 'components/Autocomplete';
import { concat, differenceWith } from 'ramda';
import { SelectField } from 'components/form/SelectField';
import { useTranslation } from 'react-i18next';
import { CACHE_AND_NETWORK } from 'lib/cache';
import { useQueryCached } from 'components/utils/graphql';
import { dateToUtc } from 'components/utils/utils';
import { kpiPlatformIcons } from 'components/KpiPlatformIcons';
import {
  AthleteOptions, CategoryOptions, KpiOptions, TagOptions, SeriesTrendState,
} from './SeriesInputs2';

type AthletePropsIn = {
  teamId: string,
  athletes?: OptionType[],
  label?: string,
  fullWidth?: boolean,
  showTags?: boolean,
  showValue?: boolean,
  enableCloseOnSelect?: boolean,
  onSelect: (selected: OptionType[]) => void,
  required?: boolean,
  withRoleOptions?: boolean,
  multiple?: boolean,
  defaultValueSelected?: boolean,
  activeAddSeries?: boolean,
  disabled?: boolean,
}
type AthleteOptionsPropsIn = {
  athletes: AthleteOptions,
  athleteList: AthleteOptions[],
  addSeries?: boolean;
  allowAthleteMultivalue: boolean,
  roles: object,
  statsType: string;
  defaultValueSelected: boolean;
  setCurrentValues: (series: SeriesTrendState) => void;
  startDate: string;
  endDate: string;
}
type CategoryOptionsPropsIn = {
  disabled: boolean,
  categories: CategoryOptions,
  categoryOptions: CategoryOptions[],
  label?: string,
  required?: boolean,
  onSelect: (selected: {id:string, name:string, color:string}[]) => void,
}
type KpiOptionsPropsIn = {
  addSeries: boolean,
  kpis: KpiOptions,
  kpiOptions: KpiOptions[],
  statsType:string,
  multiple: boolean,
  maxNumber: number,
  onChange: () => void,
  setCurrentValues: () => void;
  displayTargetIcon: boolean;
}
type TagOptionsPropsIn = {
  disabled: boolean,
  tags: TagOptions,
  tagOptions: TagOptions[],
  multiple?: boolean,
  label?: string,
  onSelect: (selected: {id:string, name:string}[]) => void,
}
type MatchSelectorPropsIn = {
  label: string,
  value: [{id:string, value: string}] | [],
  options: [{id:string, value: string}] | null,
  onSelect: (selected: OptionType[]) => void,
}

export function capitalize(name: string) {
  if (!name) return '';
  const cleanedName = name.replace(/\s+/g, ' ').trim(); // remove possible extra white spaces
  const splt = cleanedName.toLowerCase().split(' ').map((t) => t[0].toUpperCase() + t.substring(1));
  return splt.join(' ');
}

export function AthleteListSelector(props: AthletePropsIn) {
  const { t } = useTranslation();
  const {
    teamId, withRoleOptions, defaultValueSelected, activeAddSeries, label, fullWidth, required, disabled, enableCloseOnSelect, athletes, multiple, onSelect,
  } = props;
  const WHICH_QUERY = !withRoleOptions ? ATHLETES : ATHLETES_AND_ROLES;
  const { loading, error, data } = useQueryCached<{res: AthleteTypeRes}|AthleteAndRolesRes, AthleteTypeVars>(WHICH_QUERY, {
    variables: {
      teamId,
      sort: 'last_name',
      hasAthletesession: true,
      inUse: true,
    },
    ...CACHE_AND_NETWORK,
    onCompleted: (data) => {
      if (defaultValueSelected && !activeAddSeries) {
        const athleteList = data.res?.content.map((a) => ({ id: a.id, value: a.name ? capitalize(a.name) : `${t('track.widgets.labels.athleteNull')}`, groupBy: 'athlete' })) || [];
        onSelect(athleteList);
      }
    },
  });

  if (error) return <div>Error...</div>;

  const roles = loading
    ? []
    : withRoleOptions
      ? [{ id: 'team', value: 'TEAM', groupBy: 'role' }].concat((data as AthleteAndRolesRes).resRoles?.content.map((r) => ({ id: r.id, value: r.name, groupBy: 'role' })) || [])
      : [];
  const athletesList = loading
    ? []
    : data[props.withRoleOptions ? 'resAthletes' : 'res']?.content.map((a) => ({ id: a.id, value: a.name ? capitalize(a.name) : `${t('track.widgets.labels.athleteNull')}`, groupBy: 'athlete' })) || [];

  return (
    <SelectField
      autocomplete
      id="athlete-selector"
      label={label}
      fullWidth={fullWidth || false}
      loading={loading}
      disableClearable={false}
      required={required}
      disabled={disabled}
      onChange={(v) => {
        onSelect(v);
      }}
      options={concat(roles, athletesList)}
      disableCloseOnSelect={!enableCloseOnSelect}
      value={athletes !== null ? athletes : (defaultValueSelected ? athletes : [])}
      multiple={multiple}
    />
  );
}

export function AthletesListSelector(props: AthleteOptionsPropsIn) {
  const {
    athletes, athleteList, addSeries, allowAthleteMultivalue, statsType, defaultValueSelected, setCurrentValues, startDate, endDate,
  } = props;
  const { t } = useTranslation();
  const hasUserInteracted = useRef(false);

  // only players present in the team on the selected interval
  const parseYmdToUtcMs = (ymd?: string | null) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return null;
    return Date.UTC(y, m - 1, d); // 00:00:00 UTC
  };

  const filteredAthleteList = useMemo(() => {
    const startMs = parseYmdToUtcMs(startDate);
    const endMs = parseYmdToUtcMs(endDate);

    return athleteList?.filter((obj) => {
    // se è trend/team/role ecc. (non ha startDate) lo includi
      if (!('startDate' in obj)) return true;

      const playerStartMs = parseYmdToUtcMs(obj.startDate);
      const playerEndMs = parseYmdToUtcMs(obj.endDate);

      // overlap inclusivo: [playerStart, playerEnd] interseca [start, end]
      return (
        (!playerStartMs || (endMs !== null && playerStartMs <= endMs))
      && (!playerEndMs || (startMs !== null && playerEndMs >= startMs))
      );
    });
  }, [athleteList, startDate, endDate]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if ((statsType === 'summary' || statsType === 'scatter') && !addSeries) {
        setCurrentValues((prev) => {
          if (!defaultValueSelected) return { ...prev, athletes: [] };
          // if the user has interacted with the selection, keep only athletes still valid for the period
          if (hasUserInteracted.current) {
            const filteredIds = new Set(filteredAthleteList?.map((a) => a.id));
            const currentAthletes = prev.athletes ?? [];
            const kept = currentAthletes.filter((a) => filteredIds.has(a.id));
            return { ...prev, athletes: kept };
          }
          // initial load: select all filtered athletes
          return { ...prev, athletes: filteredAthleteList };
        });
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [defaultValueSelected, filteredAthleteList, statsType, startDate, endDate]);

  return (
    <SelectField
      autocomplete
      id="athlete-selector"
      label={statsType === 'summary' || statsType === 'scatter'
        ? t('stats.drawer.athletes', 'athletes')
        : t('table.athlete', 'athlete')}
      fullWidth
      disableClearable={false}
      required
      disabled={addSeries && (statsType === 'summary' || statsType === 'scatter')}
      onChange={(a) => {
        hasUserInteracted.current = true;
        if (!allowAthleteMultivalue) {
          const athlete = differenceWith((x, y) => x.id === y.id, a, athletes || []);
          setCurrentValues((prev) => ({ ...prev, athletes: athlete }));
          return;
        }
        setCurrentValues((prev) => ({ ...prev, athletes: a }));
      }}
      options={filteredAthleteList}
      disableCloseOnSelect={allowAthleteMultivalue}
      value={athletes || []}
      multiple={statsType === 'summary' || statsType === 'scatter'}
    />
  );
}

export function CategoryListSelector(props: CategoryOptionsPropsIn) {
  const {
    disabled, label, required, onSelect, categories, categoryOptions,
  } = props;

  return (
    <SelectField
      disabled={disabled}
      autocomplete
      multiple
      id="categories-selector"
      label={label || 'categories'}
      fullWidth
      required={required}
      onChange={(selected) => {
        const tmp = selected.map((s) => ({
          id: s.id,
          value: s.value,
          color: categoryOptions.find((c) => c.id === s.id)?.color || '',
        }));
        onSelect(tmp);
      }}
      options={categoryOptions || []}
      value={categories || []}
    />
  );
}

export function KpiListSelector(props: KpiOptionsPropsIn) {
  const {
    statsType,
    addSeries,
    kpis,
    kpiOptions,
    setCurrentValues,
    multiple,
    onChange,
    maxNumber,
    displayTargetIcon,
    label,
  } = props;

  const { t } = useTranslation();

  const filteredKpiOptions = kpiOptions.filter((op) => op.id !== 'total_seconds' && op.id !== 'role');

  return (
    <SelectField
      autocomplete
      optionIcons={displayTargetIcon ? kpiPlatformIcons : undefined}
      id={`KPI-${label || 'default'}`}
      fullWidth
      loading={false}
      label={label || t('stats.kpi', 'KPI')}
      required
      multiple={multiple}
      options={filteredKpiOptions || []}
      maxNumberItems={maxNumber || null}
      value={kpis || []}
      disabled={addSeries && (statsType === 'radar' || statsType === 'scatter')}
      onChange={(v) => {
        // 🔹 Custom change handler if provided
        if (onChange) {
          onChange(v);
          return;
        }

        // 🔹 Special handling for "x" and "y" KPI selectors
        if (label === 'x' || label === 'y') {
          setCurrentValues((prev) => {
            // Ensure we have at least two slots so we can safely assign x/y
            const updatedKPI = [...(prev.KPI || [null, null])];

            const index = label === 'x' ? 0 : 1;
            updatedKPI[index] = v?.[0] || null;

            // Optional: clean up if both are empty → reset KPI to []
            const allEmpty = updatedKPI.every((k) => !k);
            return { ...prev, KPI: allEmpty ? [] : updatedKPI };
          });
          return;
        }

        // 🔹 Default logic for all other usages (legacy behavior)
        if (multiple) {
          setCurrentValues((prev) => ({ ...prev, KPI: v }));
          return;
        }

        const tmpArr = (kpis || []).map((k) => k.id);
        const kpi = v.length === 1 ? v : v.filter((k) => !tmpArr.includes(k.id));

        setCurrentValues((prev) => ({ ...prev, KPI: kpi }));
      }}
    />
  );
}

export function TagListSelector(props: TagOptionsPropsIn) {
  const {
    disabled, tags, tagOptions, multiple, label, onSelect,
  } = props;

  return (
    <SelectField
      disabled={disabled}
      autocomplete
      multiple={multiple}
      id="tags-selector"
      fullWidth
      label={label || 'tags'}
      onChange={(selected) => {
        const tmp = selected.map((s) => ({
          id: s.id,
          value: s.value,
        }));
        onSelect(tmp);
      }}
      options={tagOptions || []}
      value={tags || []}
    />
  );
}

export function MatchSelector(props: MatchSelectorPropsIn) {
  // use it as MatchDistance/Match Cycle Selector
  const {
    label, value, options, onSelect,
  } = props;

  return (
    <SelectField
      fullWidth
      autocomplete
      id="athlete"
      label={label}
      value={value}
      onChange={(selected) => {
        const tmp = selected.map((s) => ({
          id: s.id,
          value: s.value,
        }));
        onSelect(tmp);
      }}
      options={options || []}
    />
  );
}

export function StartersSelector(props: MatchSelectorPropsIn) {
  const {
    label, value, onSelect,
  } = props;

  const options = [{ id: 0, value: '-' }, { id: 1, value: 'yes' }, { id: 2, value: 'no' }];

  return (
    <SelectField
      fullWidth
      id="athlete"
      label={label}
      value={value}
      onChange={(selected) => {
        onSelect(selected.target.value);
      }}
      options={options || []}
    />
  );
}
